import Controller from "./class.Controller.js";
import ViewController from "./class.ViewController.js";
import {Parsers} from "./class.TransporterParser.js";
import * as TransporterUtils from "./class.TransporterUtils.js";
import TransporterLog from "./class.TransporterLog.js";

const LOGGER = new TransporterLog({
	attributes: {
		name: "TransporterInput",
		color: "red",
	},
});

// Time it takes to load student progress before we decide to freak out.
const LOAD_TIMEOUT = 3000;

/**
 * The input controller for Transporter.
 */
export default class TransporterInput extends Controller {
	/**
	 * @param {Object} options
	 * @param {*} options.attributes.mainController
	 * @param {*} options.attributes.progress
	 */
	constructor(options) {
		super(options);

		this.reset();

		if (this.progress) {
			this.load(this.progress);
		}
	}

	/**
	 * Returns the inputController to a blank state.
	 */
	reset(resetParserCache = false) {
		this.disabled = false; // Is buffer disabled?
		this.inputs = []; // History of inputs
		this.queue = []; // Queue of future inputs
		this.elementMap = {}; // Map target strings -> InputElements
		this.motorCache = {}; // Map pointer strings -> motor responses
		this.tryComplete = false; // True if loading a progress obj that isComplete
		this.loading = false; // Is there still inputs left in the queue?

		if (resetParserCache) {
			for (let parser in Parsers) {
				if (typeof Parsers[parser].resetCache === "function") {
					Parsers[parser].resetCache();
				}
			}
		}
	}

	/**
	 * Returns the number of inputs remaining, and attempts
	 * to simulate one input per request.
	 * @param {object} options
	 * @param {boolean} options.enableAnimations
	 */
	request(options = {}) {
		if (this.queue.length === 0) {
			if (this.loading) {
				LOGGER.log("Successfully restored script state.");
				this.mainController.mainView.unhide();
				if (this.mainController.onLoadProgress) {
					this.mainController.onLoadProgress();
				}
				this.loading = false;
				setTimeout(() => {
					this.mainController.mainView.snapToBottom();
				}, 1);
			} else {
				if (!this.tryComplete && options.enableAnimations) {
					this.mainController.animationController.setEnabled(true);
				}
			}
			return;
		}

		var stuffToDo = () => {
			if (this.queue.length > 0) {
				this.loading = true;
				this.mainController.animationController.setEnabled(false);
				var input = this.queue[0];
				var element = this.elementMap[input.target];
				if (element) {
					var $el = $("#" + element.target);
					if ($el.length > 0) {
						this.queue.shift();
						this.elementMap[input.target].trigger(input.metadata);
						return true;
					} else {
						return false;
					}
				} else {
					return false;
				}
			} else {
				return false;
			}
		};

		while (true) {
			if (!stuffToDo()) {
				break;
			}
		}
	}

	/**
	 * Add a new Input to the input history.
	 * @param {Input} input
	 */
	push(input) {
		if (!this.disabled) {
			if (this.queue.length && !input.simulated) {
				LOGGER.error(
					"Human input interrupted state restore. Dropping input queue."
				);
				this.queue = [];
				this.mainController.animationController.setEnabled(true);
			}
			if (input.onCompress) {
				const {target} = input;
				const found = this.inputs.map((n) => n.target).indexOf(target);
				if (found >= 0) {
					/**
					 * If we can compress a sequence of inputs, such as the updating of an input field
					 * or the checking/unchecking of a checkbox, we should do so. We use the "target"
					 * property of an Input to determine this.
					 *
					 * "replace" will replace the old Input with the new one.
					 * "delete" will delete the old Input, without inserting the new one.
					 * The default action is to push the new Input to the buffer.
					 */
					const compressAction = input.onCompress(this.inputs[found], input);
					if (compressAction === "replace") {
						this.inputs.splice(found, 1, input);
					} else if (compressAction === "delete") {
						this.inputs.splice(found, 1);
					} else {
						this.inputs.push(input);
					}
				} else {
					this.inputs.push(input);
				}
			} else {
				this.inputs.push(input);
			}
			if (
				this.queue.length <= 0 &&
				(!this.progress || !this.progress.isComplete)
			) {
				this.mainController.onProgress(this.save());
			}
		}
	}

	/**
	 * Returns an object with a document_id, version, and inputs string.
	 */
	save() {
		return {
			document_id: this.mainController.document_id,
			version: this.mainController.version,
			inputs: {
				queue: _.map(this.inputs, (input) => {
					return _.pick(input, ["element", "metadata", "timestamp", "target"]);
				}),
				// parsed: _.mapObject(Parsers, parser => JSON.stringify(parser.cache)),
				parsed: Object.keys(Parsers).reduce((obj, key) => {
					obj[key] = JSON.stringify(Parsers[key].cache);
					return obj;
				}, {}),
				//
				motors: this.motorCache,
			},
			isComplete: false,
		};
	}

	/**
	 * Load a list of inputs.
	 *
	 * @param {*} obj
	 */
	load(obj) {
		this.tryComplete = obj.isComplete || false;
		if (
			parseInt(obj.document_id) !== parseInt(this.mainController.document_id)
		) {
			LOGGER.error("Cannot load input buffer. Document ID is incorrect.");
		} else if (
			parseInt(obj.version) !== parseInt(this.mainController.version)
		) {
			LOGGER.error("Cannot load input buffer. Version is incorrect.");
		} else {
			this.queue = this.decode(obj.inputs.queue);
			this.motorCache = obj.inputs.motors || {};
			for (let parser in obj.inputs.parsed) {
				Parsers[parser].cache = JSON.parse(obj.inputs.parsed[parser]); // What the heck did I just write lol
			}
			LOGGER.log(this.queue.length + " inputs buffered.");
			if (this.queue.length > 0) {
				// // Send a Sentry error if it takes too long to load.
				// setTimeout(() => {
				// 	if (this.mainController.mainView.isHidden()) {
				// 		LOGGER.error(
				// 			`Progress is taking longer than ${LOAD_TIMEOUT}ms to load.`,
				// 			obj
				// 		);
				// 	}
				// }, LOAD_TIMEOUT);
				this.mainController.mainView.hideFor(LOAD_TIMEOUT);
				this.mainController.animationController.setEnabled(false);
			}
		}
	}

	/**
	 * Converts generic objects to Inputs.
	 *
	 * @param {*} arr
	 */
	decode(arr) {
		var result = [];
		_.each(arr, (obj) => {
			result.push(
				new Input({
					attributes: {
						element: obj.element,
						metadata: obj.metadata,
						timestamp: obj.timestamp,
						target: obj.target,
					},
				})
			);
		});
		return result;
	}

	/**
	 * Disables new inputs from being buffered.
	 */
	disable() {
		this.disabled = true;
	}

	/**
	 * Allows inputs to be buffered.
	 */
	enable() {
		this.disabled = false;
	}

	/**
	 * Asks the user if they'd like to continue from their progress state,
	 * or if they'd like to start over.
	 */
	async checkResumePrompt() {
		if (
			this.progress &&
			this.queue.length &&
			!this.tryComplete &&
			!this.disableProgressModal
		) {
			AoPS.Grid.ProgressBar.enableAnimation(false);

			// Ask if they want to restart or continue
			const response = await TransporterUtils.asyncPrompt(
				"It looks like you're in the middle of this lesson. Would you like to continue, or start from the beginning?",
				["Continue", "Start Over"]
			);

			// If they chose Start Over reset the lesson
			if (response === 1) {
				this.mainController.reset();
			}

			AoPS.Grid.ProgressBar.enableAnimation(true);
		}
	}
}

/**
 * An input with metadata.
 */
export class Input extends Controller {
	/**
	 * @param {Object} options
	 * @param {String} options.attributes.element
	 * @param {Object} options.attributes.metadata
	 * @param {Number} options.attributes.timestamp
	 * @param {String} options.attributes.target
	 * @param {(prev, curr) => "replace"|"delete"|undefined} options.attributes.onCompress
	 * @param {*} options.model
	 */
	constructor(options) {
		super(options);
		// load information from this.model if it's available
	}
}

/**
 * Stub class for input elements.
 */
export class InputElement extends ViewController {
	/**
	 * Creates an InputElement. Target must be unique and deterministic.
	 * Recommend using Action pointerStr + any other necessary
	 * data to build the target string.
	 *
	 * @param {Object} options
	 * @param {*} options.attributes.inputController
	 * @param {*} options.attributes.className
	 * @param {*} options.attributes.tagName
	 * @param {*} options.attributes.target
	 */
	constructor(options) {
		super(options);
		this.bindFn = null;
		this.namespace = ".Transporter"; // The event namespace for jQuery
		this.events = []; // A list of binded events
		this.inputController.elementMap[this.target] = this; // Add to input element map
	}

	/**
	 * Stores an Input in the input controller.
	 *
	 * @param {Object} options
	 * @param {Function} options.inputBuilder
	 * @param {(isValid) => void} options.callback
	 * @param {Boolean} options.once
	 * @param {String} options.event
	 * @param {($event) => boolean} [options.validator]
	 */
	onInput(options) {
		var {inputBuilder, callback, once, event, validator} = options;

		if (this.target) {
			this.$el.attr("id", this.target);
		}
		this.$el.removeClass("off");

		this.bindFn = ($event, metadata) => {
			// Abort the event if the input does not pass validation.
			if (_.isFunction(validator)) {
				if (!validator($event)) {
					return callback && callback(false);
				}
			}
			const input = inputBuilder($event);
			if (metadata && metadata.simulated) {
				input.simulated = true;
			}
			this.inputController.push(input);
			if (_.isFunction(callback)) {
				callback(true);
			}
		};
		this.events.push(event);

		if (once) {
			this.$el.one(event + this.namespace, this.bindFn);
		} else {
			this.$el.on(event + this.namespace, this.bindFn);
		}
	}

	/**
	 * Disables this input, unbinds all events.
	 */
	off() {
		if (this.bindFn) {
			while (this.events.length > 0) {
				this.$el.off(this.events.pop() + this.namespace);
			}
			this.bindFn = null;
			this.$el.removeAttr("id");
			this.$el.addClass("off");
		}
	}

	/**
	 * Simulate the input.
	 */
	trigger() {
		throw new Error("trigger() is not implemented on this element.");
	}
}

/**
 * A single element which can bind to click events.
 * The Input generated by this class looks like this:
 *
 * Input.element: "Button"
 * Input.metadata: {
 * 		click: "left"
 * }
 * Input.timestamp: 1539803025741
 * Input.target: "body0body1ButtonPrompt"
 *
 * @param {Object} options
 * @param {boolean} options.compressable
 * 	If enabled, button will be treated like a check box, such that
 *  input buffer will compress two subsequent clicks into zero clicks.
 */
export class Button extends InputElement {
	constructor(options) {
		super(options);

		// The name of the event which will be used for listening/triggering.
		this.eventName = "click";
	}

	/**
	 *
	 * @param {Object} options
	 * @param {() => void} options.callback
	 * @param {Boolean} options.once
	 */
	onClick(options) {
		var {callback, once} = options || {};
		super.onInput({
			inputBuilder: (event) => {
				return this.buildClickInput(event);
			},
			callback: callback,
			once: once,
			event: this.eventName,
		});
	}

	/**
	 * When the button is clicked, this function will build
	 * the Input object that gets passed to the input controller.
	 * @param {*} event A jQuery click event.
	 */
	buildClickInput(event) {
		return new Input({
			attributes: {
				element: "Button",
				metadata: {
					click: "left",
				},
				timestamp: new Date().getTime(),
				target: this.target,
				onCompress: this.compressable ? () => "delete" : undefined,
			},
		});
	}

	/**
	 * Simulate the input.
	 *
	 * @param {*} metadata
	 * @param {string} metadata.click
	 */
	trigger(metadata) {
		if (metadata.click === "left") {
			this.$el.trigger(this.eventName, {simulated: true});
		}
	}
}

/**
 * A single element which can bind to input change events.
 * The Input generated by this class looks like this:
 *
 * Input.element: "TextInput"
 * Input.metadata: {
 * 		value: "2*pi"
 * }
 * Input.timestamp: 1539803025741
 * Input.target: "body0body1ShortAnswerProblem"
 */
export class TextInput extends InputElement {
	constructor(options) {
		super(options);
		if (this.tagName === "input") {
			this.$el.attr("type", "text");
			this.$el.attr("autocomplete", "off");
		}

		// The name of the event which will be used for listening/triggering.
		this.eventName = "input";
	}

	/**
	 *
	 * @param {Object} options
	 * @param {(isValid) => void} options.callback
	 * @param {($event) => boolean} options.validator
	 * @param {Boolean} options.once
	 */
	onChange(options) {
		var {callback, once, validator} = options || {};
		super.onInput({
			inputBuilder: (event) => {
				return this.buildChangeInput(event);
			},
			callback: (isValid) => {
				if (isValid) {
					this.$el.attr("last-value", this.$el.val());
				} else {
					this.$el.val(this.$el.attr("last-value") || "");
				}
				if (_.isFunction(callback)) {
					callback(isValid);
				}
			},
			once: once,
			event: this.eventName,
			validator: validator,
		});
	}

	/**
	 * When the button is clicked, this function will build
	 * the Input object that gets passed to the input controller.
	 * @param {*} event A jQuery click event.
	 */
	buildChangeInput(event) {
		return new Input({
			attributes: {
				element: "TextInput",
				metadata: {
					value: this.$el.val(),
				},
				timestamp: new Date().getTime(),
				target: this.target,
				onCompress: () => "replace",
			},
		});
	}

	/**
	 * Simulate the input.
	 *
	 * @param {*} metadata
	 * @param {string} metadata.value
	 */
	trigger(metadata) {
		this.$el.val(metadata.value);
		this.$el.trigger(this.eventName, {simulated: true});
	}
}
